// SHADOW MARKET™ — Global cart state.
// Persisted in localStorage so the cart survives reloads.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FREE_SHIPPING_THRESHOLD, SHIPPING_COST } from "../data/shop-products";

const STORAGE_KEY = "rebel_shadow_market_cart_v1";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [bump, setBump] = useState(0); // increments to trigger navbar icon animation

  // Persist
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  const addItem = useCallback((product, size, quantity = 1, customization = null) => {
    if (!product || !size) return;
    setItems((prev) => {
      // Customized items always create a separate line (unique key with timestamp)
      const key = customization
        ? `${product.id}::${size}::custom::${Date.now()}`
        : `${product.id}::${size}`;
      const existing = customization ? null : prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) =>
          i.key === key ? { ...i, quantity: Math.min(20, i.quantity + quantity) } : i
        );
      }
      return [
        ...prev,
        {
          key,
          id: product.id,
          slug: product.slug,
          name: product.name,
          type: product.type,
          color: product.color,
          colorHex: product.colorHex,
          size,
          price: product.price,
          proPrice: product.proPrice,
          accentColor: product.accentColor,
          quantity,
          customization: customization || null, // { baseColor, text, textColor, imageDataUrl, previewDataUrl, surcharge }
        },
      ];
    });
    setBump((n) => n + 1);
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const updateQty = useCallback((key, delta) => {
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i))
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  // Pricing helpers — visual only. Real total is recomputed by backend.
  const compute = useCallback(
    (isPro) => {
      const subtotal = items.reduce((sum, i) => {
        const base = (isPro ? i.proPrice : i.price);
        const custom = i.customization?.surcharge || 0;
        return sum + (base + custom) * i.quantity;
      }, 0);
      const shipping = subtotal >= FREE_SHIPPING_THRESHOLD || subtotal === 0 ? 0 : SHIPPING_COST;
      const discount = items.reduce(
        (sum, i) => sum + (i.price - i.proPrice) * i.quantity,
        0
      );
      const customizationTotal = items.reduce(
        (sum, i) => sum + (i.customization?.surcharge || 0) * i.quantity,
        0
      );
      return {
        subtotal: Number(subtotal.toFixed(2)),
        shipping: Number(shipping.toFixed(2)),
        discount: isPro ? Number(discount.toFixed(2)) : 0,
        customization: Number(customizationTotal.toFixed(2)),
        total: Number((subtotal + shipping).toFixed(2)),
        itemCount: items.reduce((n, i) => n + i.quantity, 0),
      };
    },
    [items]
  );

  const value = useMemo(
    () => ({
      items, isOpen, bump,
      addItem, removeItem, updateQty, clear,
      openCart, closeCart, compute,
    }),
    [items, isOpen, bump, addItem, removeItem, updateQty, clear, openCart, closeCart, compute]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within <CartProvider>");
  return ctx;
};
